import api from './axios';
import { User, Invitation, Role } from '../types/user';

export interface CompanyProfile {
  id: string;
  name: string;
  unp: string;
  address: string;
}

export const usersApi = {
  getCompanyProfile: async (signal?: AbortSignal): Promise<CompanyProfile> => {
    const response = await api.get('/company/profile', { signal });
    return response.data;
  },

  updateCompanyProfile: async (data: { name: string; unp: string; address: string }): Promise<CompanyProfile> => {
    const response = await api.patch('/company/profile', data);
    return response.data;
  },

  getCompanyUsers: async (): Promise<User[]> => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  changeRole: async (userId: string, role: Role): Promise<User> => {
    const response = await api.patch(`/auth/users/${userId}/role`, { role });
    return response.data;
  },

  removeUser: async (userId: string): Promise<void> => {
    await api.delete(`/auth/users/${userId}`);
  },

  invite: async (
    email: string,
    role: Role,
    deliveryMethod: 'email' | 'link' = 'email',
  ): Promise<{ invitation: Invitation; delivered: 'email' | 'link'; inviteUrl?: string }> => {
    const response = await api.post('/auth/invite', { email, role, deliveryMethod });
    return response.data;
  },

  getInvitationLink: async (id: string): Promise<{ inviteUrl: string }> => {
    const response = await api.get(`/auth/invite/${id}/link`);
    return response.data;
  },

  getInvitations: async (): Promise<Invitation[]> => {
    const response = await api.get('/auth/invitations');
    return response.data;
  },

  resendInvitation: async (
    id: string,
  ): Promise<{ invitation: Invitation; delivered: 'email' | 'link'; inviteUrl?: string }> => {
    const response = await api.post(`/auth/invite/${id}/resend`);
    return response.data;
  },

  deleteInvitation: async (id: string): Promise<void> => {
    await api.delete(`/auth/invite/${id}`);
  },

  setupCompany: async (companyName: string): Promise<{ accessToken: string; user: User }> => {
    const response = await api.post('/auth/setup-company', { companyName });
    return response.data;
  },

  getInvitationInfo: async (token: string): Promise<{ email: string; role: Role; companyName: string }> => {
    const response = await api.get(`/auth/invite/${token}`);
    return response.data;
  },

  acceptInvitation: async (token: string, credential: string): Promise<{ accessToken: string; user: User; needsCompany: boolean }> => {
    const response = await api.post(`/auth/invite/${token}/accept`, { credential });
    return response.data;
  },
};

export default usersApi;
